import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import { CaretDown, Check, MagnifyingGlass } from "@phosphor-icons/react";
import { Body } from "@hypercard/web-components";
import {
  CustomFieldEntity,
  useCustomFieldListOptionsQuery,
} from "@common/utils/graphql";
import { useMultiLevelListParentFieldOptionUuid } from "./hooks";
import { isChildOfMultiLevelList } from "./utils";

interface Props {
  value: number | null;
  onSelect: (listOptionId: number | null) => void;
  customField: CustomFieldEntity;
}

export const CustomFieldSelectInput = ({
  value,
  onSelect,
  customField,
}: Props) => {
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const parentFieldOptionUuid = useMultiLevelListParentFieldOptionUuid({
    customField,
    onSelect,
  });

  const { data, previousData } = useCustomFieldListOptionsQuery({
    variables: {
      searchText: searchText.length > 0 ? searchText : undefined,
      customFieldEntityId: customField.id,
      parentFieldOptionUuid: parentFieldOptionUuid
        ? parentFieldOptionUuid
        : undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const prevListOptions =
    previousData?.customFieldVisibleListOptions.__typename ===
    "QueryCustomFieldVisibleListOptionsSuccess"
      ? previousData.customFieldVisibleListOptions.data.listOptions
      : customField.listOptions ?? [];
  const listOptions =
    data?.customFieldVisibleListOptions.__typename ===
    "QueryCustomFieldVisibleListOptionsSuccess"
      ? data.customFieldVisibleListOptions.data.listOptions
      : prevListOptions;

  const selected = listOptions.find((listOption) => listOption.id === value);

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={
              isChildOfMultiLevelList(customField) && !parentFieldOptionUuid
            }
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between space-x-2 border-none bg-[#1c1c1c] text-white"
          >
            {selected ? (
              <>
                <span className="text-body-sm font-normal text-white">
                  {selected.name}
                </span>
                <CaretDown
                  weight="bold"
                  className="size-4 shrink-0 opacity-50"
                />
              </>
            ) : (
              <>
                <div className="flex items-center gap-x-2">
                  <MagnifyingGlass className="size-5 shrink-0 opacity-50" />
                  <span className="text-body-sm font-normal text-muted-foreground">{`Select ${customField.name}`}</span>
                </div>
                <CaretDown
                  weight="bold"
                  className="size-4 shrink-0 opacity-50"
                />
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={-40}
          className="w-[var(--radix-popper-anchor-width)] p-0 data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100"
        >
          <Command shouldFilter={false}>
            <CommandInput
              onValueChange={setSearchText}
              value={searchText}
              placeholder="Search"
            />
            <CommandList className="h-[200px]">
              <CommandEmpty>
                <Body size="md" weight="regular" className="text-white/70">
                  No matching options
                </Body>
              </CommandEmpty>
              <CommandGroup>
                {listOptions.map((listOption) => (
                  <CommandItem
                    key={`custom-field-${customField.id}--option-${listOption.uuid}`}
                    value={listOption.uuid}
                    onSelect={() => {
                      onSelect(listOption.id);
                      setOpen(false);
                    }}
                  >
                    <div className="flex w-full items-center justify-between">
                      <div className="flex flex-col gap-y-1 overflow-hidden">
                        <Body
                          size="sm"
                          weight="medium"
                          className="overflow-x-hidden truncate text-ellipsis whitespace-nowrap"
                        >
                          {listOption.name}
                        </Body>
                        <Body
                          size="xs"
                          weight="medium"
                          className="text-white/70"
                        >
                          {listOption.value}
                        </Body>
                      </div>
                      {listOption.id == value && (
                        <Check weight="bold" className="mx-2 size-4 shrink-0" />
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
